import {
  ApolloClient,
  ApolloError,
  ApolloLink,
  FieldFunctionOptions,
  from,
  InMemoryCache,
  ServerError,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { Observable } from '@apollo/client/utilities';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';

import { getSession } from '@lichtblick/auth';

import type { FetchResult, NextLink, Operation } from '@apollo/client/link/core';

let accessToken: string;
let isRefreshing = false;
let refreshPromise: Promise<void>;

const NoSession = 'No Session';
const UnrecoverableErrors = [NoSession];

export const isUnrecoverableError = (error: ApolloError) => {
  return UnrecoverableErrors.includes(error.networkError?.message ?? '');
};

const retryAfterRefresh = (refresh: Promise<unknown>, operation: Operation, forward: NextLink) => {
  return new Observable<FetchResult>((observer) => {
    let subscription: { unsubscribe: () => void } | undefined;

    refresh
      .then(() => {
        subscription = forward(operation).subscribe({
          next: (value: FetchResult) => observer.next(value),
          error: (error: unknown) => observer.error(error),
          complete: () => observer.complete(),
        });
      })
      .catch((error) => observer.error(error));

    return () => {
      subscription?.unsubscribe();
    };
  });
};

const errorLink = onError(({ forward, networkError, operation }) => {
  if (networkError) {
    if ([401].includes((networkError as ServerError).statusCode)) {
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = (async () => {
          try {
            const session = await getSession();

            if (!session) {
              throw Error(NoSession);
            }

            const newAccessToken = session?.access_token ?? '';

            operation.setContext({ accessToken: newAccessToken });
            accessToken = newAccessToken;
          } finally {
            isRefreshing = false;
          }
        })();
      }

      return retryAfterRefresh(refreshPromise, operation, forward);
    }
  }
});

const clientName = process.env.CLIENT_NAME || 'PM App';
const clientVersion = process.env.GIT_HASH || '1.0.0';

const authLink = setContext((operation, context) => {
  const accessTokenFromContext = context.accessToken;
  const token = accessTokenFromContext ?? accessToken;

  return {
    headers: {
      ...context.headers,
      authorization: token ? `Bearer ${token}` : '',
      'apollographql-client-name': clientName,
      'apollographql-client-version': clientVersion,
    },
  };
});

const uploadLink = process.env.NEXT_PUBLIC_API
  ? createUploadLink({
      uri: process.env.NEXT_PUBLIC_API,
      headers: {
        'GraphQL-preflight': '1',
      },
    })
  : new ApolloLink();

const merge = (
  existing: { data: unknown[] },
  incoming: { data: unknown[] },
  { args }: FieldFunctionOptions<Record<string, any>, Record<string, any>>,
) => {
  const pageNumber = args?.input ? args.input.page.number : args?.page.number;

  if (!existing || !args || pageNumber === 0) {
    return incoming;
  }

  return {
    ...incoming,
    data: [...existing.data, ...incoming.data],
  };
};

export const getClient = (token: string) => {
  accessToken = token;

  return new ApolloClient({
    ssrMode: true,
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            getWorkOrders_V1: {
              keyArgs: [],
              merge,
            },
            getWorkItems_V1: {
              keyArgs: [],
              merge,
            },
            getAppointments_V1: {
              keyArgs: ['branch'],
              merge,
            },
          },
        },
      },
    }),
    devtools: {
      enabled: process.env.NODE_ENV === 'development',
    },
    link: from([errorLink, authLink, uploadLink]),
  });
};
